import { 
  Component, 
  ViewChild, 
  ViewEncapsulation, 
  OnInit, 
  ElementRef 
} from '@angular/core';

import { 
  ActivatedRoute, 
  Router 
} from '@angular/router';


import { 
  InputType, 
  type ItemInterface, 
  type TypeAheadInterface 
} from 'src/app/common/components/form-builder/form-builder.component';

import { AppFormRequest } from 'src/app/view/app-common/form-request/app-form-request';
import { build, list, stack } from 'src/app/common/classes/request-builder';
import { LoginApiService } from 'src/app/service/api/login-api.service';
import { UserApiService } from 'src/app/service/api/user-api.service';
import { UsuariosApiService } from 'src/app/service/api/usuarios-api.service';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { environment } from 'src/environments/environment';
import type { UserModel } from 'src/app/models/usuarios/user.model';
import { SiexApiService } from 'src/app/service/api/siex-api.service';

@Component({
  selector: 'app-register',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  // Optional; only declare if you will use it. Otherwise remove.
  @ViewChild('monthlySubscriptionButton', { static: false }) monthlySubscriptionButton!: ElementRef;
  
  @ViewChild('annualSubscriptionButton', { static: false }) annualSubscriptionButton!: ElementRef;

  /** Users loaded from API (used to validate uniqueness) */
  public users: UserModel[] = [];

  public subscriptionStatus = 'guest';

  /** Client-side validation flags (used by template) */
  public errors = {
    usernameIsValid: true,
    emailIsValid: true,
  };

  /** Branding */
  public readonly logoUrl = environment.logoUrl;

  public readonly appName = environment.appName;

  /** UI state */
  public accountCreated = false;

  /** Model bound to the form-builder */
  public model = {} as UserModel;

  /** (Kept for compatibility with your form workflow) */
  public formRequest = new AppFormRequest<UserModel>();

  /** Dropdown: tipo */
  public tipos: TypeAheadInterface<string> = list(['...', null], 'Comunero', 'Aplicador');
  
  public comunidadesAutonomas: TypeAheadInterface<string> = list();

  /** Form-builder config: account data */
  public formFieldsAccount: ItemInterface<object>[] = [
    { 
        label: this.appName === 'agrogestSIEX' ? 'Email' : 'Nombre de usuario', 
        field: 'username', 
        inputType: { type: InputType.EDIT_TEXT } 
    }
  ];

  public formFieldsPassword: ItemInterface<object>[] = [
    { 
        label: 'Contraseña', 
        field: 'password', 
        inputType: { type: InputType.PASSWORD } 
    },
    { 
        label: 'Repita la contraseña', 
        field: 'password_repeat', 
        inputType: { type: InputType.PASSWORD } 
    },
  ];

  /** Form-builder config: personal data */
  public formFieldsLeft: ItemInterface<object>[] = [
    { 
        label: 'Nombre', 
        field: 'nombre', 
        inputType: { type: InputType.EDIT_TEXT } 
    },
    { 
        label: 'Apellidos', 
        field: 'apellidos', 
        inputType: { type: InputType.EDIT_TEXT } 
    },
    { 
        label: 'Comunidad Autónoma', 
        field: 'codigo_ine', 
        inputType: { type: InputType.DROPDOWN_SEARCH },
        values: this.comunidadesAutonomas,
        valuePrimaryKey: 'codigo_ine'
    },
    { 
        label: 'Email', 
        field: 'email', 
        inputType: { type: InputType.EDIT_TEXT }, 
        visible: this.appName !== 'agrogestSIEX'  
    },
    { 
        label: this.appName === 'agrogestSIEX' ?  'NIF/CIF' : 'NIF (para autónomos)', 
        field: 'nif', 
        inputType: { type: this.appName === 'agrogestSIEX' ? InputType.EDIT_TEXT : InputType.EDIT_TEXT_MASK }, 
        mask: '99999999-a', 
    },
    { 
        label: 'CIF (para SL, SA, CB o similares)', 
        field: 'cif', 
        inputType: { type: InputType.EDIT_TEXT_MASK }, 
        mask: 'a-99999999',
        visible: this.appName !== 'agrogestSIEX' 
    },
    { 
        label: 'Teléfono', 
        field: 'telefono', 
        inputType: { type: this.appName === 'agrogestSIEX' ? InputType.EDIT_TEXT : InputType.EDIT_TEXT_MASK }, 
        mask: '999-999-999' 
    },
    { 
        label: 'Tipo', 
        field: 'tipo', 
        inputType: { type: InputType.DROPDOWN }, 
        values: this.tipos,
        visible: this.appName !== 'agrogestSIEX'
    },
  ];

  /** Fast lookup (O(1)) sets for username/email uniqueness checks */
  private usernameSet = new Set<string>();
  
  private emailSet = new Set<string>();

  // -------------------------------
  // Form & Validation
  // -------------------------------
  /**
   * Basic client-side validation
   * - Required fields
   * - Password equality
   * - Username & email flags (already set in formChanges)
   */
  private readonly REQUIRED_FIELDS: Array<keyof UserModel> = [
    'username',
    'password',
    'nombre',
    'apellidos',
    'telefono',
    'codigo_ine'
  ];

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    public readonly loginApi: LoginApiService,
    public readonly userApi: UserApiService,
    public readonly usuariosApi: UsuariosApiService,
    public siexApi: SiexApiService,
  ) {
    void Router;
  }

  // -------------------------------
  // Lifecycle
  // -------------------------------
  async ngOnInit(): Promise<void> {
    this.initFormData();
    
    // Redirect if already logged in
    const currentUser = StorageManager.getUser();

    this.route.queryParamMap.subscribe(params => {
      this.subscriptionStatus = params.get('estado') ?? '';
    });

    if (currentUser?.id && this.appName !== 'agrogestSIEX') {
      this.router.navigate(['dashboard']);
      return;
    }

    // Load users for uniqueness checks
    try {
      this.users = await this.usuariosApi.usuariosGET.toPromise();
      this.usernameSet = new Set(
        this.users.map(u => (u.username ?? '').trim().toLowerCase()).filter(Boolean)
      );
      this.emailSet = new Set(
        this.users.map(u => (u.email ?? '').trim().toLowerCase()).filter(Boolean)
      );
    } catch (e) {
      // Keep UI usable even if the list fails; just skip uniqueness checks
      // (You could toast a warning here if desired)
      // console.warn('Failed to load users for uniqueness checks', e);
    }
  }

  // -------------------------------
  // Actions
  // -------------------------------
  async register(): Promise<void> {
    if (!this.validateForm()) {
      alert('Por favor, rellene todos los campos obligatorios y/o asegúrese de que las contraseñas coincidan.');
      return;
    }

    try {
      await this.loginApi.register(this.model);
      
      this.accountCreated = true;

      const STRIPE_RENDERING_DELAY = 100;

      setTimeout(() => {
        const element = this.monthlySubscriptionButton.nativeElement;
        element.setAttribute('customer-email', this.model.username);

        const element2 = this.annualSubscriptionButton.nativeElement;
        element2.setAttribute('customer-email', this.model.username);
      }, STRIPE_RENDERING_DELAY);
    
      // Fire-and-forget; if you want to await, add `await` and handle errors.
      this.userApi.sendEmailConfirmation({
        usuario: this.model.username,
        entidad: this.model.entidad,
        email: this.model.email,
        estado: '3',
      } as UserModel);
    } catch (e) {
      // You might replace this alert with a toast/snackbar
      console.error('Error on register()', e);
      alert('No se ha podido completar el registro. Inténtelo de nuevo.');
    }
  }

  goBack(): void {
    this.router.navigate(['login']);
  }

  
  validateForm(): boolean {
    // 1) Required fields
    const hasAllRequired = this.REQUIRED_FIELDS.every(f =>
      Boolean((this.model as any)[f])
    );
    
    if (!hasAllRequired) {return false;}

    // 2) CIF/NIF normalization (your original rule)
    if (this.model.cif != null) {
      (this.model as any).nif = (this.model as any).cif;
    }
    delete (this.model as any).cif;

    // 3) Passwords match
    if ((this.model as any).password !== (this.model as any).password_repeat) {
      return false;
    }

    // 4) Uniqueness checks (flags are updated in formChanges)
    if (!this.errors.emailIsValid || !this.errors.usernameIsValid) {
      return false;
    }

    return true;
  }

  /**
   * Handle incremental validations based on changed field.
   * Keeps O(1) lookups via sets and normalizes input.
   */
  formChanges(tag: string): void {
    switch (tag) {
      case 'username': {
        const normalized = (this.model.username ?? '').trim().toLowerCase();
        const syntacticallyValid = this.isValidEmail(normalized);
        
        if (this.appName === 'agrogestSIEX') {
          this.errors.usernameIsValid = syntacticallyValid && !this.usernameSet.has(normalized);
        } else {
          this.errors.usernameIsValid = normalized.length > 0 && !this.usernameSet.has(normalized);
        }
        console.log(this.errors);
        break;
      }

      case 'email': {
        const normalized = (this.model.email ?? '').trim().toLowerCase();
        const syntacticallyValid = this.isValidEmail(normalized);
        
        this.errors.emailIsValid = syntacticallyValid && !this.emailSet.has(normalized);
        break;
      }

      case 'cif': {
        (this.model as any).nif = null;
        break;
      }

      case 'nif': {
        (this.model as any).cif = null;
        break;
      }

      default:
        break;
    }
  }

  public subscribe(): void {
      window.open('https://buy.stripe.com/5kQfZigtnaqh520eVn8bS00?locale=es&prefilled_email=' + this.model.username);
  }

  // -------------------------------
  // Utils
  // -------------------------------
  /** Minimal email validation (you can swap for a stronger regex if needed) */
  private isValidEmail(value: string): boolean {
    if (!value) {return false;}
    // Simple and fast: must contain "@" and ".", with non-space chars around
    const basic = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    return basic.test(value);
  }

  private initFormData(): Promise<boolean> {
    const builds = [
      build(this.siexApi.comunidadesAutonomasGET, this.comunidadesAutonomas, 'descripcion', true)
    ];
    
    return stack(builds, () => {
      this.formRequest.update();
    });
  }
}
